<script lang="ts" setup>
  import { Icon, Button } from '@skillstown/leap';
  import { useModal } from '~~/stores/modal';

  const props = defineProps<{
    title: string;
    onSubmit?: () => any;
    onClose?: () => any;
    isLoading?: boolean;
    isSubmitDisabled?: boolean;
    isFullScreen?: boolean;
    removeFooter?: boolean;
  }>();

  const modal = useModal();

  function close(): void {
    if (!!props.onClose) {
      props.onClose();
    }
    modal.close();
  }

  const isFullScreen = computed(() =>
    props.isFullScreen === undefined ? false : props.isFullScreen
  );
</script>

<template>
  <div
    class="base-modal"
    :class="{ 'base-modal__full-screen': isFullScreen }"
  >
    <div class="base-modal__header">
      <BaseSubtitle>{{ props.title }}</BaseSubtitle>
      <div class="cursor-pointer p-[1px]">
        <svg
          @click="close"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          class="h-5 w-5"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M6 18 18 6M6 6l12 12"
          />
        </svg>
      </div>
    </div>

    <div
      class="base-modal__content-wrapper"
      :class="{
        'base-modal__content-wrapper--full-screen': isFullScreen,
        'base-modal__content-wrapper--full-screen--no-footer': props.removeFooter,
      }"
    >
      <div
        class="content-wrapper__content"
        :class="{ 'content-wrapper__content--full-screen': isFullScreen }"
      >
        <slot></slot>
      </div>
    </div>

    <div
      v-if="!props.removeFooter"
      class="base-modal__footer"
    >
      <Button
        :loading="props.isLoading"
        :disabled="props.isSubmitDisabled || isLoading"
        @click="props.onSubmit"
        icon="check"
        color="brand"
      >
        Submit
      </Button>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
  .base-modal {
    @apply rounded-md;
    @apply max-h-screen;
    @apply shadow-2xl;
    @apply bg-white;

    &__full-screen {
      @apply w-full;
      @apply h-full;
    }

    .base-modal__header {
      @apply flex items-center justify-between;
      @apply border-b border-gray-400 border-opacity-20;
      @apply px-8 py-3;
      @apply h-14;
    }

    .base-modal__content-wrapper {
      @apply overflow-y-auto;
      max-height: calc(100vh - 120px);

      &--full-screen {
        @apply h-full;
        /* Container - header - footer - border - border */
        height: calc(100% - 3.5rem - 3.5rem - 1px - 1px);

        &--no-footer {
          height: calc(100% - 3.5rem - 1px);
        }
      }

      .content-wrapper__content {
        @apply px-8 py-3;
        @apply min-h-[14rem];
        @apply overflow-y-auto;

        &--full-screen {
          @apply h-full;
        }
      }
    }

    .base-modal__footer {
      @apply flex items-center justify-end px-6 py-3;
      @apply border-t border-gray-400 border-opacity-20;
      @apply h-14;
    }
  }
</style>
